import {diffWordsWithSpace} from 'diff'

import {FC, useState, useEffect} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Form, Button, Spinner} from 'react-bootstrap'
import {showSwal} from '../../../../ui-kit'
import {useIntl} from 'react-intl'
import {getChapterReviewById, postChapterReview} from './core/_requests'

import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {useNavigate, useParams} from 'react-router-dom'
import {AgreementChapter} from './core/_models'
import './TradeDataAgreementChapterReviewPage.scss'

import SummernoteEditor from '../../../../ui-kit/SummerNote/SummerNote'



const createHighlightedDiff = (oldText: string, newText: string) => {
  const diff = diffWordsWithSpace(oldText || '', newText || '')

  const html = diff
    .map((part) => {
      if (part.added) return `<span style="background-color:#d4edda;">${part.value}</span>`
      if (part.removed)
        return `<span style="background-color:#f8d7da;text-decoration:line-through;">${part.value}</span>`
      return part.value
    })
    .join('')

  return html
}

const TradeDataAgreementChapterReviewPage: FC = () => {
  const intl = useIntl()
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const {idReview} = useParams()

  const [isLoadingAgreement, setIsLoadingAgreement] = useState(false)
  const [isLoadingButton, setIsLoadingButton] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)
  const placeholders: Record<string, string> = {
    chapter_id: 'Enter chapter ID',
    chapter_name: 'Enter chapter name',
    chapter_number: 'Enter chapter number',
    article_id: 'Enter article ID',
    article_name: 'Enter article name',
    article_number: 'Enter article number',
    article_text: 'Enter article text',
    article_text_display: 'Enter article text to display...',
    source: 'Enter source',
  }

  const [wordCount, setWordCount] = useState(0)
  const [agreementDetail, setAgreementDetail] = useState({
    agreement: '',
    xml_file: '',
  })
  const [agreementChapterOrigin, setAgreementChapterOrigin] = useState<AgreementChapter>({
    id: '',
    chapter_number: '',
    chapter_name: '',
    article_number: '',
    article_name: '',
    article_text: '',
    article_text_display: '',
    chapter_id: '',
    article_id: '',
    source: '',
    nwords_new: null,
    rta_id: null,
  })
  const [agreementChapterPending, setAgreementChapterPending] = useState<AgreementChapter>({
    id: '',
    chapter_number: '',
    chapter_name: '',
    article_number: '',
    article_name: '',
    article_text: '',
    article_text_display: '',
    chapter_id: '',
    article_id: '',
    nwords_new: null,
    rta_id: null,
    rta_chapter_id: null,
    filename: '',
    source: '',
  })

  const convertFromHTML = (html: string): string => {
    const text = html
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
    return text
  }

  const countWordsFromHtml = (html: string): number => {
    const text = convertFromHTML(html)
    if (!text) return 0
    return text.split(' ').length
  }

  const [highlightedDiffDisplay, setHighlightedDiffDisplay] = useState('')

  const fetchOptionAgreement = async () => {
    try {
      setIsLoadingAgreement(true)

      const detailRes = await getChapterReviewById(axiosPrivate, {idReview})

      if (detailRes?.data) {
        const {agreement, xml_file, chapter_pending_review, chapter_origin} = detailRes.data

        setAgreementDetail({agreement, xml_file})
        setAgreementChapterPending(chapter_pending_review)
        setWordCount(chapter_pending_review?.nwords_new)

        if (chapter_origin) {
          setAgreementChapterOrigin(chapter_origin)

          const oldDisplay = chapter_origin?.article_text_display ?? ''
          const newDisplay = chapter_pending_review?.article_text_display ?? ''

          const diffDisplayHTML = createHighlightedDiff(oldDisplay, newDisplay)
          setHighlightedDiffDisplay(diffDisplayHTML)
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoadingAgreement(false)
    }
  }

  const isDifferent = (originVal: any, newVal: any) => {
    if (!originVal && !newVal) return false
    return String(originVal || '').trim() !== String(newVal || '').trim()
  }

  useEffect(() => {
    fetchOptionAgreement()
  }, [])

  // Add or Edit
  const handleValueChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const {name, value} = e.target
    setAgreementChapterPending((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const validateRequiredFields = () => {
    const requiredFields = ['article_name']

    const isValid = requiredFields.every((key) => {
      const val = (agreementChapterPending as any)[key]
      return val !== undefined && val !== null && String(val).trim() !== ''
    })

    setIsFormValid(isValid)
  }

  useEffect(() => {
    validateRequiredFields()
  }, [agreementChapterPending])

  const handleSubmit = () => {
    const body = {
      rta_id: agreementChapterPending.rta_id,
      rta_chapter_id: agreementChapterPending.rta_chapter_id ?? null,
      chapter_number: agreementChapterPending.chapter_number || '',
      chapter_name: agreementChapterPending.chapter_name || '',
      article_number: agreementChapterPending.article_number || '',
      article_name: agreementChapterPending.article_name || '',
      article_text: agreementChapterPending.article_text || '',
      article_text_display: agreementChapterPending.article_text_display || '',
      title: agreementDetail.agreement || '',
      filename: agreementChapterPending.filename || '',
      source: agreementChapterPending.source || '',
      chapter_id: agreementChapterPending.chapter_id,
      article_id: agreementChapterPending.article_id,
      nwords_new: wordCount,
    }

    showSwal({
      title: 'Are you sure you want to submit this chapter?',
      icon: 'question',
    }).then((result) => {
      if (!result.isConfirmed) return // exit if user cancels

      setIsLoadingButton(true)

      postChapterReview(axiosPrivate, agreementChapterPending.id, body)
        .then((res) => {
          if (res.status === 200) {
            showSwal('Data reviewed', undefined, 'success').then(() => {
              navigate(`/agreement/${agreementChapterPending.rta_id}`)
            })
          }
        })
        .catch((err) => {
          console.error(err)
          showSwal('Error', 'Failed to review data', 'error')
        })
        .finally(() => {
          setIsLoadingButton(false)
        })
    })
  }

  const handleCancel = () => {
    showSwal({
      title: 'Are you sure you want to cancel?',
      icon: 'question',
    }).then((result) => {
      if (result.isConfirmed) {
        navigate(`/agreement/${agreementChapterPending.rta_id}`)
      }
    })
  }
  // End of Add or Edit

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: 'Trade Data Agreement',
            path: 'agreement',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: agreementDetail.agreement ?? '-',
            path: `agreement/${agreementChapterPending.rta_id}`,
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        Review Chapter
      </PageTitle>
      <div className='TradeDataAgreementChapterReviewPage'>
        <Row className='mb-5'>
          <Col>
            <Card className='mb-5'>
              <Card.Header className='card-header-stretch'>
                <Card.Title className='w-100'>
                  <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                    <div className='title d-flex align-items-center'>
                      <span className='fa fa-file-alt px-2'></span>
                      <h3 className='text-gray-700'>RTA's Information</h3>
                    </div>
                  </div>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {!isLoadingAgreement ? (
                  <div className='row'>
                    <div className='col-md-12 mb-3'>
                      <Form.Label className='text-primary me-0 ps-0' column>
                        Agreement Name
                      </Form.Label>
                      <div className='data-value'>{agreementDetail.agreement}</div>
                      {agreementDetail.xml_file ? (
                        <a
                          href={agreementDetail.xml_file}
                          target='_blank'
                          rel='noreferrer'
                          className='text-link'
                        >
                          check XML File
                        </a>
                      ) : (
                        ''
                      )}
                    </div>
                  </div>
                ) : (
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{height: '50vh'}}
                  >
                    <Spinner animation='border' role='status' variant='primary' className='me-3' />
                    <div className='loading-text'>
                      Loading
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
            <Card>
              <Card.Header className='card-header-stretch'>
                <Card.Title className='w-100'>
                  <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                    <div className='title d-flex align-items-center'>
                      <span className='fa fa-file-alt px-2'></span>
                      <h3 className='text-gray-700'>Review Chapter</h3>
                    </div>
                  </div>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {!isLoadingAgreement ? (
                  <Form className='form-agreement'>
                    <div className='row'>
                      <div className='chapter-review'>
                        {agreementChapterOrigin.id !== '' && (
                          <div className='chapter-information chapter-information-before'>
                            <div className='row mb-5'>
                              <div className='col-lg-12'>
                                <h2 className='information-title'>Origin Source</h2>
                              </div>
                            </div>
                            <div className='row'>
                              <div className='col-12'>
                                <h3>Source Information</h3>
                              </div>
                              <div className='col-md-12'>
                                <Form.Label className='text-primary me-0 ps-0' column>
                                  Link
                                </Form.Label>
                                <Form.Control
                                  type='text'
                                  name='source'
                                  placeholder={placeholders['source']}
                                  value={agreementChapterOrigin.source ?? ''}
                                  onChange={handleValueChange}
                                  readOnly
                                />
                              </div>
                            </div>
                            <hr />
                            <div className='row mb-5'>
                              <div className='col-lg-12'>
                                <h2 className='information-title'>Origin Chapter</h2>
                              </div>
                            </div>
                            <div className='row'>
                              <div className='col-12'>
                                <h3>Chapter Information</h3>
                              </div>
                              <div className='col-md-12'>
                                <Form.Label className='text-primary me-0 ps-0' column>
                                  Chapter Name
                                </Form.Label>
                                <Form.Control
                                  type='text'
                                  name='chapter_name'
                                  placeholder={placeholders['chapter_name']}
                                  value={agreementChapterOrigin.chapter_name ?? ''}
                                  onChange={handleValueChange}
                                  readOnly
                                />
                              </div>
                              <div className='col-md-4'>
                                <Form.Label className='text-primary me-0 ps-0' column>
                                  Chapter Number
                                </Form.Label>
                                <Form.Control
                                  type='text'
                                  name='chapter_number'
                                  placeholder={placeholders['chapter_number']}
                                  value={agreementChapterOrigin.chapter_number ?? ''}
                                  onChange={handleValueChange}
                                  readOnly
                                />
                              </div>
                              <div className='col-md-4'>
                                <Form.Label className='text-primary me-0 ps-0' column>
                                  Chapter Id
                                </Form.Label>
                                <Form.Control
                                  type='text'
                                  name='chapter_id'
                                  placeholder={placeholders['chapter_id']}
                                  value={agreementChapterOrigin.chapter_id ?? ''}
                                  onChange={handleValueChange}
                                  readOnly
                                />
                              </div>
                            </div>
                            <hr />
                            <div className='row'>
                              <div className='col-12'>
                                <h3>Article Information</h3>
                              </div>
                              <div className='col-md-12 mb-3'>
                                <Form.Label className='text-primary me-0 ps-0' column>
                                  <span className='required'>Article Name</span>
                                </Form.Label>
                                <Form.Control
                                  type='text'
                                  name='article_name'
                                  placeholder={placeholders['article_name']}
                                  value={agreementChapterOrigin.article_name ?? ''}
                                  onChange={handleValueChange}
                                  readOnly
                                />
                              </div>
                              <div className='col-md-4 mb-3'>
                                <Form.Label className='text-primary me-0 ps-0' column>
                                  Article Number
                                </Form.Label>
                                <Form.Control
                                  type='text'
                                  name='article_number'
                                  placeholder={placeholders['article_number']}
                                  value={agreementChapterOrigin.article_number ?? ''}
                                  onChange={handleValueChange}
                                  readOnly
                                />
                              </div>
                              <div className='col-md-4 mb-3'>
                                <Form.Label className='text-primary me-0 ps-0' column>
                                  Article Id
                                </Form.Label>
                                <Form.Control
                                  type='text'
                                  name='article_id'
                                  placeholder={placeholders['article_id']}
                                  value={agreementChapterOrigin.article_id ?? ''}
                                  onChange={handleValueChange}
                                  readOnly
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        <div className='chapter-information chapter-information-after'>
                          <div className='row mb-5'>
                            <div className='col-lg-12'>
                              <h2 className='information-title'>Pending Review Source</h2>
                            </div>
                          </div>
                          <div className='row'>
                            <div className='col-12'>
                              <h3>Source Information</h3>
                            </div>
                            <div className='col-md-12'>
                              <Form.Label className='text-primary me-0 ps-0' column>
                                <span
                                  className={
                                    isDifferent(
                                      agreementChapterOrigin.source,
                                      agreementChapterPending.source
                                    )
                                      ? 'label-highlight'
                                      : ''
                                  }
                                >
                                  Link
                                </span>
                              </Form.Label>
                              <Form.Control
                                type='text'
                                name='source'
                                placeholder={placeholders['source']}
                                value={agreementChapterPending.source ?? ''}
                                onChange={handleValueChange}
                              />
                            </div>
                          </div>
                          <hr />
                          <div className='row mb-5'>
                            <div className='col-lg-12'>
                              <h2 className='information-title'>Pending Review Chapter</h2>
                            </div>
                          </div>
                          <div className='row'>
                            <div className='col-12'>
                              <h3>Chapter Information</h3>
                            </div>
                            <div className='col-md-12'>
                              <Form.Label className='text-primary me-0 ps-0' column>
                                <span
                                  className={
                                    isDifferent(
                                      agreementChapterOrigin.chapter_name,
                                      agreementChapterPending.chapter_name
                                    )
                                      ? 'label-highlight'
                                      : ''
                                  }
                                >
                                  Chapter Name
                                </span>
                              </Form.Label>
                              <Form.Control
                                type='text'
                                name='chapter_name'
                                placeholder={placeholders['chapter_name']}
                                value={agreementChapterPending.chapter_name ?? ''}
                                onChange={handleValueChange}
                              />
                            </div>
                            <div className='col-md-4'>
                              <Form.Label className='text-primary me-0 ps-0' column>
                                <span
                                  className={
                                    isDifferent(
                                      agreementChapterOrigin.chapter_number,
                                      agreementChapterPending.chapter_number
                                    )
                                      ? 'label-highlight'
                                      : ''
                                  }
                                >
                                  Chapter Number
                                </span>
                              </Form.Label>
                              <Form.Control
                                type='text'
                                name='chapter_number'
                                placeholder={placeholders['chapter_number']}
                                value={agreementChapterPending.chapter_number ?? ''}
                                onChange={handleValueChange}
                              />
                            </div>
                            <div className='col-md-4'>
                              <Form.Label className='text-primary me-0 ps-0' column>
                                <span
                                  className={`required ${
                                    isDifferent(
                                      agreementChapterOrigin.chapter_id,
                                      agreementChapterPending.chapter_id
                                    )
                                      ? 'label-highlight'
                                      : ''
                                  }`}
                                >
                                  Chapter Id
                                </span>
                              </Form.Label>
                              <Form.Control
                                type='text'
                                name='chapter_id'
                                placeholder={placeholders['chapter_id']}
                                value={agreementChapterPending.chapter_id ?? ''}
                                onChange={handleValueChange}
                              />
                            </div>
                          </div>
                          <hr />
                          <div className='row'>
                            <div className='col-12'>
                              <h3>Article Information</h3>
                            </div>
                            <div className='col-md-12 mb-3'>
                              <Form.Label className='text-primary me-0 ps-0' column>
                                <span
                                  className={`required ${
                                    isDifferent(
                                      agreementChapterOrigin.article_name,
                                      agreementChapterPending.article_name
                                    )
                                      ? 'label-highlight'
                                      : ''
                                  }`}
                                >
                                  Article Name
                                </span>
                              </Form.Label>
                              <Form.Control
                                type='text'
                                name='article_name'
                                placeholder={placeholders['article_name']}
                                value={agreementChapterPending.article_name ?? ''}
                                onChange={handleValueChange}
                              />
                            </div>
                            <div className='col-md-4 mb-3'>
                              <Form.Label className='text-primary me-0 ps-0' column>
                                <span
                                  className={
                                    isDifferent(
                                      agreementChapterOrigin.article_number,
                                      agreementChapterPending.article_number
                                    )
                                      ? 'label-highlight'
                                      : ''
                                  }
                                >
                                  Article Number
                                </span>
                              </Form.Label>
                              <Form.Control
                                type='text'
                                name='article_number'
                                placeholder={placeholders['article_number']}
                                value={agreementChapterPending.article_number ?? ''}
                                onChange={handleValueChange}
                              />
                            </div>
                            <div className='col-md-4 mb-3'>
                              <Form.Label className='text-primary me-0 ps-0' column>
                                <span
                                  className={`required ${
                                    isDifferent(
                                      agreementChapterOrigin.article_id,
                                      agreementChapterPending.article_id
                                    )
                                      ? 'label-highlight'
                                      : ''
                                  }`}
                                >
                                  Article Id
                                </span>
                              </Form.Label>
                              <Form.Control
                                type='text'
                                name='article_id'
                                placeholder={placeholders['article_id']}
                                value={agreementChapterPending.article_id ?? ''}
                                onChange={handleValueChange}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <hr />
                    <div className='row'>
                      <div className='col-md-12'>
                        <Form.Label className='text-primary me-0 ps-0 label-with-info' column>
                          Article Text Display
                          <div className='word-count'>
                            {agreementChapterOrigin.nwords_new !== wordCount && (
                              <span className='diff-word-count'>
                                {agreementChapterOrigin.nwords_new}
                              </span>
                            )}
                            <span>{wordCount}</span>
                          </div>
                        </Form.Label>
                        {agreementChapterPending.rta_chapter_id &&
                          agreementChapterOrigin.article_text_display !==
                            agreementChapterPending.article_text_display && (
                            <div
                              className='mb-3 p-3 border rounded bg-light'
                              dangerouslySetInnerHTML={{__html: highlightedDiffDisplay}}
                            />
                          )}
                        <SummernoteEditor
                          value={agreementChapterPending.article_text_display || ''}
                          onChange={(html) => {
                            setAgreementChapterPending((prev) => ({
                              ...prev,
                              article_text_display: html,
                            }))
                            setWordCount(countWordsFromHtml(html))
                          }}
                        />
                      </div>
                      <div className='row pt-5'>
                        <div className='col-lg-12 d-flex justify-content-end gap-3'>
                          <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                            Cancel
                          </Button>
                          <Button
                            variant='primary'
                            onClick={handleSubmit}
                            disabled={isLoadingButton || !isFormValid}
                          >
                            {isLoadingButton ? (
                              <>
                                <Spinner animation='border' size='sm' className='me-2' />
                                Submit
                              </>
                            ) : (
                              'Submit'
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Form>
                ) : (
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{height: '50vh'}}
                  >
                    <Spinner animation='border' role='status' variant='primary' className='me-3' />
                    <div className='loading-text'>
                      Loading
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default TradeDataAgreementChapterReviewPage